import { useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, Button, Alert, Chip, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { aiSettingsApi } from '../../api/aiSettings.api';
import { extractApiError } from '../../api/error';

/**
 * Document AI configuration — the Anthropic API key + model used to auto-extract data from uploaded
 * documents (RFQ quotations, purchase invoices, manufacturer price lists). No server access needed.
 */
export function AiSettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ai-settings'], queryFn: () => aiSettingsApi.get() });
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [show, setShow] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () => aiSettingsApi.update({
      ...(apiKey ? { apiKey } : {}),
      ...(model ? { model } : {}),
    }),
    onSuccess: () => { setMsg({ kind: 'success', text: 'Document AI settings saved.' }); setApiKey(''); setModel(''); qc.invalidateQueries({ queryKey: ['ai-settings'] }); },
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });
  const clearKey = useMutation({
    mutationFn: () => aiSettingsApi.update({ apiKey: '' }),
    onSuccess: () => { setMsg({ kind: 'success', text: 'Stored key cleared (falls back to server env if set).' }); qc.invalidateQueries({ queryKey: ['ai-settings'] }); },
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });

  const sourceLabel = data?.source === 'settings' ? 'App settings' : data?.source === 'env' ? 'Server env' : 'Not set';

  return (
    <>
      <PageHeader title="Document AI" subtitle="Anthropic API key used to auto-extract data from uploaded documents (RFQ quotations, purchase invoices, price lists)." />
      <Card sx={{ maxWidth: 720 }}>
        <CardHeader
          title="Extraction provider"
          action={data && <Chip size="small" label={data.configured ? `Configured · ${sourceLabel}` : 'Not configured'} color={data.configured ? 'success' : 'default'} />}
        />
        <CardContent>
          <Stack spacing={2}>
            {msg && <Alert severity={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}
            {!data?.configured && (
              <Alert severity="warning">
                No API key is set, so documents cannot be auto-read — users must enter quotation numbers, dates and rates manually. Paste an Anthropic API key below to enable auto-extraction.
              </Alert>
            )}
            {data?.configured && data.keyHint && (
              <Typography variant="body2" color="text.secondary">Current key: <b>{data.keyHint}</b> (source: {sourceLabel})</Typography>
            )}
            <TextField
              label="Anthropic API key"
              type={show ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={data?.configured ? 'Enter a new key to replace the current one' : 'sk-ant-...'}
              fullWidth
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((s) => !s)} edge="end">{show ? <VisibilityOff /> : <Visibility />}</IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={data?.model ?? 'claude-sonnet-5'}
              helperText={`Leave blank to keep the current model (${data?.model ?? 'claude-sonnet-5'}).`}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <Button variant="contained" disabled={save.isPending || (!apiKey && !model)} onClick={() => save.mutate()}>Save</Button>
              {data?.configured && data.source === 'settings' && (
                <Button color="inherit" disabled={clearKey.isPending} onClick={() => clearKey.mutate()}>Clear stored key</Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              The key is stored securely on the server and never shown again in full. Get a key from console.anthropic.com.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
